import { observer } from 'mobx-react'
import React, { Component } from 'react'
import ReactModal from 'react-modal'

import { CloseIcon } from '@flowwai-frontend/design-system/icons'
import { colors } from '@flowwai-frontend/shared/style-guide'
import { isMobileDevice } from '@flowwai-frontend/shared/utils/web'

import ErrorBoundaryWrapper from '../ErrorBoundaryWrapper/ErrorBoundaryWrapper'

import { MODAL_BACKGROUND_NO_SCROLL_CLASSNAME } from './constants'
import { CloseIconWrapper } from './styledComponents'
import './styles.css'

export interface ReactBaseModalContainerProps {
   hideCloseIcon?: boolean
   onClickCloseIcon?: (event?: React.MouseEvent | React.KeyboardEvent) => void
   shouldCloseOnOverlayClick?: boolean
   onModalOpen?: () => void
   onModalClose?: () => void
   shouldReturnFocusAfterClose?: boolean
   customCloseIcon?: (closeIconProps) => void
   shouldCloseOnEsc?: boolean
   animateWithSlide?: boolean

   [x: string]: any
}

// TODO: add callbacks for open

const ReactBaseModalContainer = observer(
   class ReactBaseModalContainer extends Component<
      ReactBaseModalContainerProps
   > {
      static defaultProps = {
         hideCloseIcon: false,
         closeIconProps: {},
         shouldCloseOnOverlayClick: true
      }

      UNSAFE_componentWillMount() {
         ReactModal.setAppElement('body')
      }

      onModalOpen = (): void => {
         const { onModalOpen } = this.props

         onModalOpen && onModalOpen()
         document.body.classList.add(MODAL_BACKGROUND_NO_SCROLL_CLASSNAME)
      }

      onModalClose = (): void => {
         const { onModalClose } = this.props

         onModalClose && onModalClose()
         document.body.classList.remove(MODAL_BACKGROUND_NO_SCROLL_CLASSNAME)
      }

      onRequestClose = (): void => {
         const { onClickCloseIcon, onRequestClose } = this.props
         onClickCloseIcon && onClickCloseIcon()
         onRequestClose && onRequestClose()
      }

      getAnimatedChildClassName = (): string => {
         const { isOpen } = this.props

         const zoomAnimationName = isOpen
               ? 'open-modal-with-zoom'
               : 'close-modal-with-zoom',
            slideAnimationName = isOpen
               ? 'open-modal-with-slide'
               : 'close-modal-with-slide'

         return isMobileDevice || this.props.animateWithSlide
            ? slideAnimationName
            : zoomAnimationName
      }

      getAnimatedOverlayClassName = (): string => {
         const { isOpen } = this.props

         const animationName = isOpen
            ? 'open-modal-overlay-with-animation'
            : 'close-modal-overlay-with-animation'

         return animationName
      }

      render(): React.ReactNode {
         const {
            children,
            hideCloseIcon,
            onClickCloseIcon,
            closeIconClass,
            closeIconProps,
            shouldCloseOnOverlayClick,
            isOpen,
            className,
            overlayClassName,
            customCloseIcon,
            shouldCloseOnEsc = true,
            ...other
         } = this.props

         return (
            <ReactModal
               // NOTE: `closeTimeoutMS` should always be less than the close animation's `duration` value
               closeTimeoutMS={150}
               onAfterOpen={this.onModalOpen}
               onAfterClose={this.onModalClose}
               onRequestClose={onClickCloseIcon}
               shouldCloseOnEsc={shouldCloseOnEsc}
               shouldFocusAfterRender={true}
               shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
               {...other}
               isOpen={isOpen}
               className={`${className} modal-animation-properties ${this.getAnimatedChildClassName()}`}
               overlayClassName={`${overlayClassName} modal-animation-properties ${this.getAnimatedOverlayClassName()}`}
            >
               {hideCloseIcon ? null : (
                  <CloseIconWrapper
                     className={closeIconClass}
                     onClick={this.onRequestClose}
                  >
                     {customCloseIcon ? (
                        customCloseIcon(closeIconProps)
                     ) : (
                        <CloseIcon
                           fill={colors['blue-gray-400']}
                           {...closeIconProps}
                        />
                     )}
                  </CloseIconWrapper>
               )}
               <ErrorBoundaryWrapper
                  falBackErrorViewContainerStyles={'rounded-[16px]'}
                  shouldShowActionItem={false}
               >
                  {children}
               </ErrorBoundaryWrapper>
            </ReactModal>
         )
      }
   }
)

export { ReactBaseModalContainer }
